import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const Responsibility = () => {
  const [responsibilities, setResponsibilities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  // roles is array here
  const [formValues, setFormValues] = useState({ name: '', roles: [] });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [allResponsibilities, setAllResponsibilities] = useState([]);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState([]);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || '10', 10);

  useEffect(() => {
    fetchResponsibilities();
  }, []);

  useEffect(() => {
    filterAndPaginateResponsibilities();
  }, [allResponsibilities, searchQuery, selectedRole, currentPage]);

  useEffect(() => {
    // Load selected responsibilities when role changes
    if (selectedRole) {
      loadRoleResponsibilities();
    } else {
      setSelectedResponsibilities([]);
    }
  }, [selectedRole, allResponsibilities]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get('/admin/api/role');
        setRoles(res.data?.roles || []);
      } catch (err) {
        toastError('Failed to load roles');
      }
    };
    fetchRoles();
  }, []);

  const fetchResponsibilities = async () => {
    try {
      const res = await axios.get(`/admin/api/responsibility/all?limit=1000`);
      const { responsibility = [] } = res.data;
      setAllResponsibilities(responsibility);
    } catch (err) {
      console.error('Failed to fetch responsibilities:', err);
    }
  };

  const filterAndPaginateResponsibilities = () => {
    let filtered = [...allResponsibilities];

    // Apply search filter only (not role filter)
    if (searchQuery.trim()) {
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Calculate pagination
    const totalCount = filtered.length;
    const totalPagesCount = Math.ceil(totalCount / limit);
    setTotalPages(totalPagesCount);

    // Apply pagination
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedResults = filtered.slice(startIndex, endIndex);

    setResponsibilities(paginatedResults);
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      roles: { required: true, isArray: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        ...formValues,
        roles: formValues.roles.join(',') // already array of role IDs
      };

      if (editingId) {
        await axios.put(`/admin/api/responsibility/${editingId}`, payload);
        toastSuccess('Responsibility updated successfully!');
      } else {
        await axios.post('/admin/api/responsibility', payload);
        toastSuccess('Responsibility added successfully!');
      }

      setCurrentPage(1);
      fetchResponsibilities();
      handleCancel();
    } catch (err) {
      console.error('Failed to save responsibility:', err);
      toastError('Failed to save responsibility');
    }
  };

  const handleEdit = (item) => {
    setFormValues({
      name: item.name || '',
      roles: item.roles?.map((r) => r._id) || [], // array of IDs
    });
    setEditingId(item._id);
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this responsibility?')) return;
    try {
      await axios.delete(`/admin/api/responsibility/${id}`);
      toastSuccess('Responsibility deleted successfully!');
      if (responsibilities.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchResponsibilities();
      }
    } catch (err) {
      console.error('Failed to delete responsibility:', err);
      toastError('Failed to delete responsibility');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', roles: [] });
    setEditingId(null);
    setErrors({});
  };

  const handleFormChange = (updatedValues) => {
    // updatedValues.roles expected as array
    setFormValues(updatedValues);
  };

  // Load responsibilities for selected role
  const loadRoleResponsibilities = () => {
    if (!selectedRole) return;
    
    const assignedResponsibilities = allResponsibilities
      .filter(responsibility => 
        responsibility.roles && responsibility.roles.some(role => role._id === selectedRole)
      )
      .map(responsibility => responsibility._id);
    
    setSelectedResponsibilities(assignedResponsibilities);
  };

  // Check if a responsibility is assigned to the selected role
  const isResponsibilityAssigned = (responsibility) => {
    return selectedResponsibilities.includes(responsibility._id);
  };

  // Handle checkbox change for role assignment (local state only)
  const handleCheckboxChange = (responsibility, isChecked) => {
    if (!selectedRole) {
      toastError('Please select a role first');
      return;
    }

    setSelectedResponsibilities(prev => {
      if (isChecked) {
        return [...prev, responsibility._id];
      } else {
        return prev.filter(id => id !== responsibility._id);
      }
    });
  };

  // Save selected responsibilities to the role
  const handleSaveResponsibilities = async () => {
    if (!selectedRole) {
      toastError('Please select a role first');
      return;
    }

    try {
      await axios.post('/admin/api/role/save-responsibilities', {
        roleId: selectedRole,
        responsibilities: selectedResponsibilities
      });

      toastSuccess('Responsibilities assigned successfully!');
      // Refresh the data to reflect changes
      fetchResponsibilities();
    } catch (err) {
      console.error('Failed to save responsibilities:', err);
      toastError('Failed to save responsibilities');
    }
  };

  return (
    <div className="container mt-2">
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Role & Responsibility</h5>
        </div>

        <div className="card-body p-0">
          {/* Controls above table */}
          <div className="d-flex justify-content-between align-items-center p-1 border-bottom">
            <select
              className="form-select w-25"
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
            
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={handleSaveResponsibilities}
                disabled={!selectedRole}
              >
                Save
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={() => {
                  setShowModal(true);
                  setFormValues({ name: '', roles: [] });
                  setEditingId(null);
                  setErrors({});
                }}
              >
                + Add
              </button>
            </div>
          </div>

          <table
            className="table table-bordered table-sm mb-0 fs-6" 
          >
            <thead className="table-light text-center">
              <tr>
                <th className="px-3">Module Name</th>
                <th className="px-3">Sub Module</th>
                <th className="px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {responsibilities.length > 0 ? (
                responsibilities.map((item, idx) => (
                  <tr key={item._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <input 
                          type="checkbox" 
                          className="form-check-input ml-0" 
                          checked={isResponsibilityAssigned(item)}
                          onChange={(e) => handleCheckboxChange(item, e.target.checked)}
                          disabled={!selectedRole}
                        />
                        <span className="ml-4">{item.name}</span>
                      </div>
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-3 text-muted">
                    No responsibilities found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                    color: currentPage === page ? 'white' : '#C44C23',
                    border: '1px solid #C44C23',
                  }}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Responsibility' : 'Add Responsibility'}
        fields={[
          {
            name: 'roles',
            label: 'Role',
            placeholder: 'Select Roles',
            type: 'select',
            isMulti: true,
            options: roles.map((role) => ({ label: role.name, value: role._id })),
            required: true,
          },
          {
            name: 'name',
            label: 'Responsibility',
            placeholder: 'Enter Responsibility Name',
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={handleFormChange}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name', 'roles']}
      />
    </div>
  );
};

export default Responsibility;
